import type { IntegrationProvider } from "@rally/agreed";
import {
  Alert,
  BrandMark,
  breakpoints,
  ChoiceChips,
  Col,
  Icon,
  ListRow,
  ListSection,
  PageHeader,
  ScrollArea,
  SkeletonRows,
  Stack,
  Status,
  Surface,
  Text,
  useTheme,
} from "@rally/ui-kit";
import { useCallback, useEffect, useState } from "react";
import { useStack } from "../chat/panel/panels.tsx";
import { say } from "../localization/say.ts";
import { read } from "../net/rally.ts";
import { holdsCredentialsOf, PROVIDER_CARDS } from "./a-provider.ts";
import { COMING, INTEGRATION_KINDS, type IntegrationKind, SEARCH_SUBJECTS } from "./an-integration.ts";
import type { HeldCredential, HeldSearchKey } from "./held.ts";
import { ConnectForm, OFFERED } from "./integration-connect.tsx";
import { type Held, integrationPanel, ServiceMark, statusToneOf } from "./integration-panel.tsx";
import { providerName, scopeWord, statusWord } from "./integration-said.ts";
import { ProviderCard } from "./provider-card.tsx";
import { SearchKeys } from "./search-keys.tsx";
import { saidWhy } from "./signing-in.ts";

type Filter = "all" | IntegrationKind;

export function Integrations({ me }: { me: string }) {
  const theme = useTheme();
  const narrow = theme.isAtMost(breakpoints.phone);
  const stack = useStack();
  const [filter, setFilter] = useState<Filter>("all");
  const [credentials, setCredentials] = useState<HeldCredential[] | null>(null);
  const [searchKeys, setSearchKeys] = useState<HeldSearchKey[] | null>(null);
  const [services, setServices] = useState<Held[] | null>(null);
  const [connecting, setConnecting] = useState<IntegrationProvider | null>(null);
  const [wrong, setWrong] = useState<string | null>(null);

  const again = useCallback(() => {
    read<HeldCredential[]>("shared.providers")
      .then(setCredentials)
      .catch((cause: unknown) => setWrong(saidWhy(cause)));
    read<HeldSearchKey[]>("search.keys")
      .then(setSearchKeys)
      .catch((cause: unknown) => setWrong(saidWhy(cause)));
    read<Held[]>("integration.mine", {})
      .then(setServices)
      .catch(() => setServices([]));
  }, []);
  useEffect(again, [again]);

  const shows = (kind: IntegrationKind) => filter === "all" || filter === kind;
  const held = credentials ?? [];
  const _connected =
    held.length +
    (searchKeys ?? []).length +
    (services ?? []).filter((one) => one.status === "connected").length;

  const coming = (kind: IntegrationKind) =>
    COMING.filter((one) => one.kind === kind).map((one, index, all) => (
      <ListRow
        key={one.id}
        testID={`integration-coming-${one.id}`}
        title={one.name}
        value={say(one.whatKey)}
        lead={
          <Col style={{ opacity: 0.55 }}>
            {one.mark ? (
              <BrandMark name={one.mark} size={22} tone="brand" />
            ) : (
              <Icon name={one.icon ?? "puzzle"} size={22} color="text.secondary" />
            )}
          </Col>
        }
        action={<Status tone="neutral">{say("integrations.soon")}</Status>}
        last={index === all.length - 1}
      />
    ));

  return (
    <Stack gap={4} testID="details-integrations">
      <PageHeader title={say("you.integrations")} blurb={say("integrations.blurb")} />

      <ScrollArea horizontal showsHorizontalScrollIndicator={false} style={{ flexGrow: 0 }}>
        <ChoiceChips
          value={filter}
          onChange={setFilter}
          options={[
            { value: "all" as const, label: say("integrations.kind.all"), testID: "integrations-all" },
            ...INTEGRATION_KINDS.map((kind) => ({
              value: kind,
              label: say(`integrations.kind.${kind}`),
              testID: `integrations-${kind}`,
            })),
          ]}
        />
      </ScrollArea>

      {wrong ? <Alert tone="danger">{wrong}</Alert> : null}

      {credentials === null || services === null ? <SkeletonRows rows={4} /> : null}

      {credentials !== null && shows("ai") ? (
        <ListSection title={say("integrations.kind.ai")} blurb={say("integrations.aiWhy")}>
          {PROVIDER_CARDS.map((subject) => (
            <ProviderCard
              key={subject.provider}
              subject={subject}
              held={held.filter((one) => holdsCredentialsOf(subject).includes(one.provider))}
              onChanged={again}
            />
          ))}
        </ListSection>
      ) : null}

      {services !== null && shows("social") ? (
        <ListSection title={say("integrations.kind.social")} blurb={say("integrations.socialWhy")}>
          {OFFERED.map((provider) => {
            const mine = services.find((one) => one.provider === provider);
            return mine ? (
              <ListRow
                key={provider}
                title={providerName(provider)}
                value={`${mine.label} · ${(mine.scopes ?? []).map(scopeWord).join(", ")}`}
                lead={<ServiceMark provider={provider} />}
                testID={`integration-${provider}`}
                onPress={() => stack.open(integrationPanel(mine, me))}
                action={
                  <Col style={{ flexDirection: "row", alignItems: "center", gap: theme.space[2] }}>
                    <Status tone={statusToneOf(mine.status)}>{statusWord(mine.status)}</Status>
                    <Icon name="chevron-right" size={16} color="text.tertiary" />
                  </Col>
                }
              />
            ) : (
              <ListRow
                key={provider}
                title={providerName(provider)}
                value={say(`integrations.what.${provider}`)}
                lead={<ServiceMark provider={provider} />}
                testID={`integration-offer-${provider}`}
                onPress={() => setConnecting(provider)}
                action={
                  <Col style={{ flexDirection: "row", alignItems: "center", gap: theme.space[2] }}>
                    {narrow ? null : (
                      <Text typeName="caption" color="text.tertiary">
                        {say("providers.notSetUp")}
                      </Text>
                    )}
                    <Icon name="chevron-right" size={16} color="text.tertiary" />
                  </Col>
                }
              />
            );
          })}
          {coming("social")}
        </ListSection>
      ) : null}

      {credentials !== null && shows("search") ? (
        <ListSection title={say("integrations.kind.search")} blurb={say("integrations.searchWhy")}>
          {SEARCH_SUBJECTS.map((subject) => (
            <SearchKeys
              key={subject.provider}
              only={subject}
              held={searchKeys ?? []}
              loading={searchKeys === null}
              onChanged={again}
            />
          ))}
        </ListSection>
      ) : null}

      {services !== null && shows("shopping") ? (
        <ListSection title={say("integrations.kind.shopping")}>{coming("shopping")}</ListSection>
      ) : null}

      {services !== null && shows("travel") ? (
        <ListSection title={say("integrations.kind.travel")}>{coming("travel")}</ListSection>
      ) : null}

      <Surface
        kind="panel"
        width={520}
        visible={connecting !== null}
        onDismiss={() => setConnecting(null)}
        title={say("integrations.connect")}
        testID="integration-connect-panel"
      >
        <Col style={{ padding: 20 }}>
          <ConnectForm
            first={connecting}
            onDone={() => {
              setConnecting(null);
              again();
            }}
          />
        </Col>
      </Surface>
    </Stack>
  );
}
